import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Shuffle, Zap, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import SEO from '../utils/seo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { id: '', name: 'All Categories', color: '#FF4500' },
  { id: 'movies', name: 'Movies & TV', color: '#dc2626' },
  { id: 'automotive', name: 'Automotive', color: '#2563eb' },
  { id: 'music', name: 'Music', color: '#7c3aed' },
  { id: 'sports', name: 'Sports', color: '#16a34a' },
  { id: 'art', name: 'Art & Design', color: '#ec4899' },
  { id: 'travel', name: 'Travel & Places', color: '#0891b2' },
  { id: 'vintage', name: 'Vintage & Retro', color: '#ca8a04' },
];

const RandomPage = () => {
  const [category, setCategory] = useState('');
  const [displayPosters, setDisplayPosters] = useState([]);
  const [spinning, setSpinning] = useState(false);
  const [winner, setWinner] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [randomGrid, setRandomGrid] = useState([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const stripRef = useRef(null);
  const poolRef = useRef([]);

  useEffect(() => {
    SEO.applyNoIndex('Random Poster Wheel | PosterFire', '/random');
    return () => SEO.resetDefaults();
  }, []);

  useEffect(() => {
    axios.get(`${API}/products/random/pick?limit=24`).then(r => setRandomGrid(r.data)).catch(() => {});
  }, []);

  const loadPool = useCallback(async (cat) => {
    try {
      const url = cat ? `${API}/products/random/pick?category=${cat}&limit=50` : `${API}/products/random/pick?limit=50`;
      const res = await axios.get(url);
      poolRef.current = res.data;
    } catch { poolRef.current = []; }
  }, []);

  useEffect(() => { loadPool(category); }, [category, loadPool]);

  const spin = async () => {
    if (spinning || poolRef.current.length === 0) {
      if (poolRef.current.length === 0) toast.error('No posters found for this category');
      return;
    }

    setShowResult(false);
    setWinner(null);
    setSpinning(true);

    await loadPool(category);
    const pool = poolRef.current;
    if (pool.length === 0) { setSpinning(false); return; }

    const winnerIdx = Math.floor(Math.random() * pool.length);
    const winnerPoster = pool[winnerIdx];

    // Build strip of ~30 items ending with winner
    const strip = [];
    for (let i = 0; i < 28; i++) {
      strip.push(pool[Math.floor(Math.random() * pool.length)]);
    }
    strip.push(winnerPoster); // index 28
    strip.push(pool[Math.floor(Math.random() * pool.length)]); // buffer
    setDisplayPosters(strip);

    requestAnimationFrame(() => {
      if (stripRef.current) {
        stripRef.current.style.transition = 'none';
        stripRef.current.style.transform = 'translateX(0px)';

        requestAnimationFrame(() => {
          if (stripRef.current) {
            // Each card: 260px wide + 16px gap = 276px
            const cardWidth = 276;
            const containerWidth = stripRef.current.parentElement?.offsetWidth || 960;
            const targetOffset = (28 * cardWidth) - (containerWidth / 2) + 130;
            stripRef.current.style.transition = 'transform 4s cubic-bezier(0.15, 0.85, 0.35, 1)';
            stripRef.current.style.transform = `translateX(-${targetOffset}px)`;
          }
        });
      }
    });

    setTimeout(() => {
      setWinner(winnerPoster);
      setShowResult(true);
      setSpinning(false);
    }, 4200);
  };

  const refreshGrid = () => {
    axios.get(`${API}/products/random/pick?limit=24`).then(r => setRandomGrid(r.data)).catch(() => {});
  };

  const selectedCat = CATEGORIES.find(c => c.id === category) || CATEGORIES[0];

  return (
    <div className="min-h-screen bg-gray-950" data-testid="random-page">
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-orange-500/10 via-transparent to-transparent" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4 pb-6 relative">

          {/* Compact title near nav */}
          <div className="flex items-center gap-2.5 mb-6">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-red-500 to-orange-500 flex items-center justify-center">
              <Shuffle className="w-4 h-4 text-white" />
            </div>
            <h1 className="text-xl sm:text-2xl font-black text-white tracking-tight">
              RANDOM <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-400 to-orange-400">PICK</span>
            </h1>
            <p className="hidden sm:block text-gray-500 text-sm ml-2">
              Spin and discover your next favorite poster
            </p>
          </div>

          {/* Roulette Strip — Large */}
          <div className="relative mx-auto mb-6">
            {/* Center marker */}
            <div className="absolute top-0 left-1/2 -translate-x-1/2 z-10 w-0 h-0 border-l-[14px] border-r-[14px] border-t-[20px] border-l-transparent border-r-transparent border-t-orange-500" />
            <div className="absolute bottom-0 left-1/2 -translate-x-1/2 z-10 w-0 h-0 border-l-[14px] border-r-[14px] border-b-[20px] border-l-transparent border-r-transparent border-b-orange-500" />

            {/* Gradient masks */}
            <div className="absolute left-0 top-0 bottom-0 w-32 bg-gradient-to-r from-gray-950 to-transparent z-10 pointer-events-none" />
            <div className="absolute right-0 top-0 bottom-0 w-32 bg-gradient-to-l from-gray-950 to-transparent z-10 pointer-events-none" />

            {/* Strip container */}
            <div className="overflow-hidden rounded-2xl border border-gray-800 bg-gray-900/50 backdrop-blur-sm py-10" data-testid="roulette-strip">
              {displayPosters.length > 0 ? (
                <>
                  <div
                    ref={stripRef}
                    className="flex gap-4 px-6"
                    style={{ width: 'max-content' }}
                  >
                    {displayPosters.map((poster, i) => (
                      <div
                        key={`${poster.id}-${i}`}
                        className={`flex-shrink-0 w-[260px] rounded-xl overflow-hidden border-2 transition-all duration-300 ${
                          showResult && winner && poster.id === winner.id && i === 28
                            ? 'border-orange-500 shadow-lg shadow-orange-500/40 scale-105'
                            : 'border-gray-800'
                        }`}
                      >
                        <div className="poster-image-wrap">
                          <img
                            src={poster.image_url}
                            alt={`${poster.title} ${poster.category || ''} poster`}
                            className="w-full aspect-[3/4] object-cover poster-image"
                            draggable="false"
                            onContextMenu={(e) => e.preventDefault()}
                          />
                        </div>
                        <div className="p-3 bg-gray-900">
                          <p className="text-[10px] text-gray-500 uppercase tracking-wider">{poster.category}</p>
                          <p className="text-sm text-white font-medium truncate">{poster.title}</p>
                          <p className="text-base font-bold text-orange-400 mt-0.5">${poster.price?.toFixed(2)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                  {/* Winner actions — inside the wheel */}
                  {showResult && winner && (
                    <div className="flex items-center justify-center gap-3 mt-6 px-6 animate-slide-up" data-testid="winner-actions">
                      <span className="text-orange-400 text-xs font-bold uppercase tracking-[0.2em] mr-2">Your Pick:</span>
                      <span className="text-white font-bold text-sm truncate max-w-[200px]">{winner.title}</span>
                      <span className="text-orange-400 font-black text-sm">${winner.price?.toFixed(2)}</span>
                      <Link
                        to={winner.slug ? `/products/${winner.slug}` : `/product/${winner.id}`}
                        className="px-6 py-2.5 bg-gradient-to-r from-red-500 to-orange-500 text-white font-bold text-sm rounded-lg hover:from-red-600 hover:to-orange-600 transition-all"
                        data-testid="winner-view-btn"
                      >
                        View Poster
                      </Link>
                      <button
                        onClick={spin}
                        className="px-6 py-2.5 border border-gray-700 text-gray-300 font-bold text-sm rounded-lg hover:bg-gray-800 hover:text-white transition-all"
                        data-testid="spin-again-btn"
                      >
                        Spin Again
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-20 text-gray-500">
                  <Shuffle className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p className="text-base">Pick a category and hit SPIN!</p>
                </div>
              )}
            </div>
          </div>

          {/* Controls: Category + Spin — Below the wheel */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
            <div className="relative" data-testid="category-dropdown">
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex items-center gap-3 px-6 py-3.5 bg-gray-900 border border-gray-700 rounded-xl text-white font-medium min-w-[220px] hover:border-gray-500 transition-colors"
                data-testid="category-select-btn"
              >
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: selectedCat.color }} />
                <span className="flex-1 text-left text-sm">{selectedCat.name}</span>
                <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              {dropdownOpen && (
                <div className="absolute bottom-full left-0 right-0 mb-2 bg-gray-900 border border-gray-700 rounded-xl overflow-hidden shadow-2xl z-20">
                  {CATEGORIES.map(cat => (
                    <button
                      key={cat.id}
                      onClick={() => { setCategory(cat.id); setDropdownOpen(false); }}
                      className={`w-full flex items-center gap-3 px-5 py-3 text-sm text-left transition-colors ${
                        category === cat.id ? 'bg-gray-800 text-white font-bold' : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                      }`}
                      data-testid={`random-cat-${cat.id || 'all'}`}
                    >
                      <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: cat.color }} />
                      {cat.name}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <button
              onClick={spin}
              disabled={spinning}
              className={`group relative px-12 py-3.5 rounded-xl font-black text-lg uppercase tracking-wider transition-all overflow-hidden ${
                spinning
                  ? 'bg-gray-700 text-gray-400 cursor-not-allowed'
                  : 'bg-gradient-to-r from-red-500 to-orange-500 text-white hover:from-red-600 hover:to-orange-600 shadow-lg shadow-orange-500/30 hover:shadow-orange-500/50 hover:scale-105'
              }`}
              data-testid="spin-btn"
            >
              <span className="flex items-center gap-2">
                <Zap className={`w-5 h-5 ${spinning ? 'animate-pulse' : 'group-hover:animate-bounce'}`} />
                {spinning ? 'Spinning...' : 'SPIN!'}
              </span>
            </button>
          </div>

        </div>
      </div>

      {/* Random Discovery Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-black text-white tracking-tight">Discover Random Posters</h2>
            <p className="text-gray-500 text-sm mt-1">A fresh selection every time you visit</p>
          </div>
          <button
            onClick={refreshGrid}
            className="flex items-center gap-2 px-4 py-2 bg-gray-800 border border-gray-700 text-gray-300 text-sm font-medium rounded-lg hover:bg-gray-700 hover:text-white transition-colors"
            data-testid="refresh-grid-btn"
          >
            <Shuffle className="w-4 h-4" />
            Shuffle
          </button>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="random-grid">
          {randomGrid.map((poster) => (
            <Link
              key={poster.id}
              to={poster.slug ? `/products/${poster.slug}` : `/product/${poster.id}`}
              className="group block bg-gray-900 border border-gray-800 rounded-lg overflow-hidden hover:border-gray-600 transition-all hover:scale-[1.02]"
              data-testid={`random-poster-${poster.id}`}
            >
              <div className="poster-image-wrap">
                <img
                  src={poster.image_url}
                  alt={`${poster.title} ${poster.category || ''} poster`}
                  className="w-full aspect-[3/4] object-cover poster-image"
                  loading="lazy"
                  draggable="false"
                  onContextMenu={(e) => e.preventDefault()}
                />
              </div>
              <div className="p-2">
                <p className="text-[9px] uppercase tracking-wider text-gray-600">{poster.category}</p>
                <h3 className="text-xs text-gray-300 font-medium truncate group-hover:text-orange-400 transition-colors">{poster.title}</h3>
                <p className="text-sm font-bold text-orange-400 mt-0.5">${poster.price?.toFixed(2)}</p>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

export default RandomPage;
